import React from 'react';
import { ArrowRight } from 'lucide-react';
import { TrackingLink } from '@/lib/marketing';

interface CompactRowCTA {
  text: string;
  href: string;
  /** marketing TrackingLink content slug */
  content: string;
}

interface CompactFeatureRowProps {
  /** Mini-heading (NOT h2-scale). Optional for single-beat groups where the
      group headline already serves as the heading. */
  heading?: React.ReactNode;
  /** 1-2 line body copy */
  body: React.ReactNode;
  /** Optional italic "brief line" rendered under the body (e.g., "Review smarter. Remember longer.") */
  tagline?: React.ReactNode;
  /**
   * TwicPics screenshot basename WITHOUT extension or path
   * (e.g., "JDS-Issue-Spotter"). Mutually exclusive with `visual`.
   */
  image?: string;
  /** Optional cache-bust suffix appended to the TwicPics URL (e.g., "&v=20260209") */
  imageVersion?: string;
  /** Alt text for the screenshot (required when `image` is set) */
  imageAlt?: string;
  /** Custom visual node (e.g., interactive demo). Mutually exclusive with `image`. */
  visual?: React.ReactNode;
  /** Which side the visual sits on (desktop). Text takes the opposite side. */
  side?: 'left' | 'right';
  /** Optional small text-link CTA */
  cta?: CompactRowCTA;
  /** Optional anchor id for in-page navigation (carries scroll-margin offset) */
  id?: string;
  /** Called with a `media:` path when an image is clicked (opens fullscreen zoom) */
  onImageZoom?: (mediaPath: string) => void;
}

const TWIC_BASE = 'https://jdsimplified.twic.pics/images/screenshots';

/**
 * CompactFeatureRow — a half-height (~py-10 rhythm) feature row used inside a
 * FeatureGroup. Renders one mini-heading, a 1-2 line body, an optional small
 * text-link CTA, and exactly ONE visual (screenshot OR custom node) on the
 * chosen side. Mobile stacks text-first, visual-second.
 */
export function CompactFeatureRow({
  heading,
  body,
  tagline,
  image,
  imageVersion = '',
  imageAlt = '',
  visual,
  side = 'right',
  cta,
  id,
  onImageZoom,
}: CompactFeatureRowProps) {
  // Desktop ordering: text always reads first in DOM (mobile-first), then we
  // place the visual left/right via order classes on desktop.
  const textOrder = side === 'left' ? 'md:order-2' : 'md:order-1';
  const visualOrder = side === 'left' ? 'md:order-1' : 'md:order-2';

  const renderImage = () => {
    if (!image) return null;
    // TwicPics `media:` paths cache by source path, so the version must ride on
    // the path itself as a `?v=` query (the same convention the logos use) —
    // otherwise the fullscreen-zoom render (data-twic-src, which has no other
    // cache-bust) keeps serving the old upload. imageVersion arrives as "&v=X"
    // for the direct-URL src/srcSet below; normalize the leading &/? to "?".
    const versionQuery = imageVersion ? `?${imageVersion.replace(/^[?&]/, '')}` : '';
    const mediaPath = `media:images/screenshots/${image}.png${versionQuery}`;
    return (
      <div
        className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 hover:border-[#F37022]/30 cursor-zoom-in transform hover:scale-[1.02]"
        onClick={() => onImageZoom?.(mediaPath)}
      >
        <div className="relative bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
          <img
            src={`${TWIC_BASE}/${image}.png?twic=v1/resize=590/quality=85/output=auto${imageVersion}`}
            srcSet={`
              ${TWIC_BASE}/${image}.png?twic=v1/resize=606/quality=85/output=auto${imageVersion} 606w,
              ${TWIC_BASE}/${image}.png?twic=v1/resize=590/quality=85/output=auto${imageVersion} 590w,
              ${TWIC_BASE}/${image}.png?twic=v1/resize=1180/quality=85/output=auto${imageVersion} 1180w
            `}
            sizes="(max-width: 768px) 606px, 590px"
            data-twic-src={mediaPath}
            data-twic-transform="quality=85/output=auto/resize=1180"
            loading="lazy"
            alt={imageAlt}
            className="w-full h-auto screenshot-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        </div>
      </div>
    );
  };

  return (
    <div id={id} className="py-8 md:py-10" style={id ? { scrollMarginTop: '6rem' } : undefined}>
      <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        {/* Text */}
        <div className={`order-1 ${textOrder}`}>
          {heading && (
            <h3 className="text-xl md:text-2xl font-bold text-black dark:text-white mb-3">
              {heading}
            </h3>
          )}
          <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            {body}
          </p>
          {tagline && (
            <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 mt-3 italic">
              {tagline}
            </p>
          )}
          {cta && (
            <TrackingLink
              href={cta.href}
              campaign="homepage_features"
              content={cta.content}
              className="inline-flex items-center gap-1.5 mt-4 text-[#F37022] hover:text-[#E35D10] font-medium transition-colors"
            >
              {cta.text}
              <ArrowRight className="w-4 h-4" />
            </TrackingLink>
          )}
        </div>

        {/* Visual — exactly one of image OR custom node */}
        <div className={`order-2 ${visualOrder} flex justify-center`}>
          <div className="w-full max-w-md">{image ? renderImage() : visual}</div>
        </div>
      </div>
    </div>
  );
}
